<template>
  <h2 class="tw-mb-6">
    {{ $t("connection-page-description") }}
  </h2>

  <div class="tw-dark:bg-gray-900 tw-bg-white tw-p-10 tw-antialiased">
    <ol class="tw-relative tw-ml-4 tw-border-l tw-border-primary-grey-100">
      <li v-for="(step, i) in steps" :key="i" class="tw-mb-10 tw-ml-11">
        <span class="tw-absolute -tw-left-9">
          <component :is="step.icon"></component>
        </span>
        <h3
          class="tw-text-gray-900 tw-mb-1 tw-flex tw-items-center tw-text-lg tw-font-semibold"
        >
          {{ step.heading }}
        </h3>
        <p
          class="tw-text-gray-500 tw-text-gray-400 tw-mb-4 tw-text-base tw-font-normal"
        >
          {{ step.label }}
        </p>
        <div v-if="step.showList" class="tw-ml-1">
          <button
            class="tw-flex tw-items-center tw-justify-between tw-text-primary-grey-700"
            type="button"
            :data-test-id="`settings-connection-more-details-${step.id}`"
            @click="toggleSection(step.id)"
          >
            <cb-icon-chevron-down
              :width="16"
              class="tw-mr-1 tw-transition-transform"
              :class="activeStep === step.id ? 'tw-rotate-180' : 'tw-rotate-0'"
            />
            {{ $t("more-details") }}
          </button>
          <div v-if="activeStep === step.id">
            <ul v-if="step.id === '1'" class="tw-mx-5 tw-list-disc tw-py-2">
              <li>
                {{ $t("copy-email-address-lead-in") }}

                <div
                  class="tw-relative tw-my-2 tw-flex tw-items-center tw-bg-primary-grey-50 tw-pl-3"
                >
                  {{ azureAccounts[0].id }}{{ NDR_EMAIL }}
                  <t-copy-button
                    class="tw-m-2.5"
                    :value="`${azureAccounts[0].id}${NDR_EMAIL}`"
                    :label="t('copy')"
                    test-id="settings-connection-copy-ndr-email"
                  />
                </div>
              </li>
              <li>
                {{ $t("go-to") }}
                <t-link
                  :is-external="true"
                  href="https://purview.microsoft.com/settings/application-settings/datalifecyclemanagement"
                  :data-test-id="`settings-connection-go-to-${step.id}`"
                  string="https://purview.microsoft.com/settings/application-settings/datalifecyclemanagement"
                />
              </li>
              <li>
                <div v-dompurify-html="$t('select-exchange-legacy')" />
              </li>
              <li>
                <div
                  v-dompurify-html="$t('send-undeliverable-journal-report')"
                />
              </li>
              <li>
                <div v-dompurify-html="$t('select-save')" />
              </li>
            </ul>
            <ul v-if="step.id === '2'" class="tw-mx-5 tw-list-disc tw-py-2">
              <li>
                {{ $t("copy-email-address-lead-in") }}
                <div
                  class="tw-my-2 tw-flex tw-items-center tw-bg-primary-grey-50 tw-pl-3"
                >
                  {{ azureAccounts[0].id }}{{ GRAPH_IN_EMAIL }}
                  <t-copy-button
                    class="tw-m-2.5"
                    :value="`${azureAccounts[0].id}${GRAPH_IN_EMAIL}`"
                    :label="t('copy')"
                    test-id="settings-connection-copy-graph-email"
                  />
                </div>
              </li>
              <li>
                {{ $t("go-to") }}
                <!-- TODO: UPDATE WITH THE ID -->
                <t-link
                  :is-external="true"
                  :href="`https://purview.microsoft.com/datalifecyclemanagement/exchange/journalrules?tid=${azureAccounts[0].tenantID}`"
                  :data-test-id="`settings-connection-go-to-${step.id}`"
                  string="https://purview.microsoft.com/datalifecyclemanagement/exchange/journalrules"
                />
              </li>
              <li>
                <div v-dompurify-html="$t('new-rule')" />
              </li>
              <li>
                <div v-dompurify-html="$t('send-journal-report-to')" />
              </li>
              <li>
                <div v-dompurify-html="$t('select-everyone-all-messages')" />
              </li>
              <li>
                <div v-dompurify-html="$t('enter-journal-rule-name')" />
              </li>
              <li>
                <div v-dompurify-html="$t('next-and-submit')" />
              </li>
            </ul>
            <ul v-if="step.id === '3'" class="tw-mx-5 tw-list-disc tw-py-2">
              <li>
                {{ $t("copy-link-lead-in") }}
                <div
                  class="tw-my-2 tw-flex tw-items-center tw-bg-primary-grey-50 tw-pl-3"
                  :data-test-id="`settings-connection-outlook-copy-url-${step.id}`"
                >
                  https://protect.titanhq.com/manifest.xml
                  <t-copy-button
                    class="tw-m-2.5"
                    value="https://protect.titanhq.com/manifest.xml"
                    :label="t('copy')"
                    :test-id="`settings-connection--outlook-copy-url-button-${step.id}`"
                  />
                </div>
              </li>
              <li>
                {{ $t("go-to-the") }}
                <t-link
                  :is-external="true"
                  href="https://admin.microsoft.com/Adminportal/Home#/Settings/IntegratedApps"
                  :data-test-id="`settings-connection-go-to-${step.id}`"
                  :string="$t('microsoft-admin-center')"
                />
              </li>
              <li>
                {{ $t("follow-instruction-add-in") }}
                <t-link
                  :is-external="true"
                  :href="EXTERNAL_DOCS_LINKS.OUTLOOK_ADD_IN_DEPLOY_INSTRUCTIONS"
                  :data-test-id="`settings-connection-go-to-outlook-add-in-deploy-instructions-${step.id}`"
                  :string="$t('learn-more')"
                />
              </li>
            </ul>
            <ul v-if="step.id === '4'" class="tw-mx-5 tw-list-disc tw-py-2">
              <li>
                {{ $t("using-st-outbound") }}
                <t-link
                  :is-external="true"
                  href="https://support.titanhq.com/en/index-en.html?contextId=outbound-connector-azure"
                  :data-test-id="`settings-connection-go-to-read-more-${step.id}`"
                  :string="$t('learn-more')"
                />
              </li>
              <li>
                {{ $t("link-lock-enabled") }}
              </li>
            </ul>
          </div>
        </div>
      </li>
    </ol>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { storeToRefs } from "pinia";
import { useAzureStore } from "@/store/azure.store";
import { useI18n } from "vue-i18n";
import { EXTERNAL_DOCS_LINKS } from "@/constants/externalDocsLinks.ts";
import TLink from "@/components/TLink";
import TCopyButton from "@/components/TCopyButton/TCopyButton.vue";
import { getGraphInEmail, getNdrEmail } from "@/config/runtimeConfig";

const { t } = useI18n();

const NDR_EMAIL = getNdrEmail();
const GRAPH_IN_EMAIL = getGraphInEmail();

type Step = {
  id: string;
  heading: string;
  label: string;
  icon: string;
  showList: boolean;
};

const steps: Step[] = [
  {
    id: "1",
    heading: t("step-1"),
    label: t("add-ndr"),
    icon: "cb-icon-email",
    showList: true,
  },
  {
    id: "2",
    heading: t("step-2"),
    label: t("add-journaling-rule"),
    icon: "cb-icon-mail-send",
    showList: true,
  },
  {
    id: "3",
    heading: t("step-3"),
    label: t("install-thq-addin"),
    icon: "cb-icon-reporting",
    showList: true,
  },
  {
    id: "4",
    heading: t("step-4"),
    label: t("disable-ll-add-connector"),
    icon: "cb-icon-report-manager",
    showList: true,
  },
];

const azureStore = useAzureStore();
const { azureAccounts } = storeToRefs(azureStore);

const activeStep = ref("");
const toggleSection = (id: string) => {
  if (activeStep.value === id) {
    activeStep.value = "";
  } else {
    activeStep.value = id;
  }
};
</script>
